import { FunnelIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { FilterBadgesList } from '@/components/FilterBadgesList';
import UnAuthorizedMessage from '@/components/Permission/UnauthorizedMessage';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import ErrorCard from '@/components/UI/ErrorCard';
import LoadingDeveloperApplications from '@/components/UI/loading/LoadingDeveloperApplications';
import SimpleToggle from '@/components/UI/Toggle/SimpleToggle';
import { formConfigurationMapping, type TFormConfig } from '@/features/ApplicationForm/FormConfig';
import { useApplicationForm } from '@/hooks/ReactQueryHooks/useApplicationFromQuery';
import { usePermission } from '@/hooks/usePermission';
import { EPermission } from '@/types/Permission';
import { isObjectEmpty } from '@/utils';
import { ApplicationNav } from '../ApplicationNav';
import { ApplicationsListProvider, useApplicationsListContext } from './ApplicationsListProvider';

const ApplicationsListTable = () => {
  const { applicationFormId } = useParams<string>();
  const [showArchived, setShowArchived] = useState(false);
  const isInitialized = useRef(false);
  const { data, filterBadges, toggleFilterModal, removeFilterHandler, fetchApplications, applyFiltersHandler } =
    useApplicationsListContext();

  const fetchApplicationForm = useApplicationForm({
    formId: applicationFormId!,
  });
  const applicationForm = fetchApplicationForm?.data?.data;

  const applicationFormConfig = formConfigurationMapping.find((fcm) => fcm.formCode === applicationForm?.templateCode) as TFormConfig;

  // Initialize with non-archived applications by default
  useEffect(() => {
    if (!isInitialized.current) {
      applyFiltersHandler({ archived: 'false' }, {});
      isInitialized.current = true;
    }
  }, [applyFiltersHandler]);

  const handleArchiveToggle = () => {
    const newShowArchived = !showArchived;
    setShowArchived(newShowArchived);

    if (newShowArchived) {
      applyFiltersHandler({ archived: 'true' }, {});
    } else {
      applyFiltersHandler({ archived: 'false' }, {});
    }
  };

  if (fetchApplications.isLoading) {
    return <LoadingDeveloperApplications />;
  }

  if (!applicationFormConfig) {
    return (
      <div className='my-5 h-screen/2 flex items-center'>
        <ErrorCard
          errorMsg='Oops! This page is currently under construction. Please check back soon.'
          errorTitle='Page Under Construction'
        />
      </div>
    );
  }

  const ApplicationListComponent = applicationFormConfig.applicationsListComponent;

  if (!ApplicationListComponent) {
    return (
      <div className='my-5 h-screen/2 flex items-center'>
        <ErrorCard
          errorMsg='Oops! This page is currently under construction. Please check back soon.'
          errorTitle='Page Under Construction'
        />
      </div>
    );
  }
  return (
    <div className='relative'>
      <div className='flex flex-col gap-2 px-4 pt-4 border-t'>
        <div className='flex flex-col flex-wrap gap-2 items-start'>
          <div className='flex gap-2 items-center'>
            <Button label='Filter' btnStyle='neutral' btnSize='md' icon={<FunnelIcon className='h-5 w-5' />} onClick={toggleFilterModal} />
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-600'>Show Archived</span>
              <SimpleToggle enabled={showArchived} handleToggle={handleArchiveToggle} />
            </div>
          </div>
          {!isObjectEmpty(filterBadges) && <FilterBadgesList displayFilters={filterBadges} handleRemoveFilter={removeFilterHandler} />}
        </div>
        {!isObjectEmpty(filterBadges) && <span className='m-0 text-neutralColor-normal mb-2'>{data?.meta?.count} applications found.</span>}
      </div>
      {fetchApplications.isSuccess && data && (
        <div className='m-4'>
          <ApplicationListComponent />
        </div>
      )}
      {!fetchApplications.isFetching && fetchApplications.isError && (
        <div className='flex-grow flex justify-center items-center text-neutralColor-normal h-screen/2'>
          <ErrorCard
            errorTitle='Fetch Error'
            errorMsg='We are unable to pull the list of applications from the database at this point. Please try again later. '
          />
        </div>
      )}
    </div>
  );
};

export const ApplicationsList = () => {
  const { hasPermission } = usePermission();

  if (!hasPermission([EPermission.LIST_APPLICATIONS])) {
    return <UnAuthorizedMessage title='Unauthorized' message='You do not have permission to view this application form applications.' />;
  }
  return (
    <>
      <ApplicationNav />
      <div className='flex flex-col w-full bg-white'>
        <div className='border-neutralColor-light flex justify-between rounded-t-lg'>
          <AccentTitle title='Applications' />
        </div>
        <ApplicationsListProvider>
          <ApplicationsListTable />
        </ApplicationsListProvider>
      </div>
    </>
  );
};
